/**
 * Central sports configuration
 * Single source of truth for all sport-related constants and metadata
 */

import sportsData from './sports.json'

export enum SPORT_ID {
  UNDEFINED = 0,
  CRICKET = 1,
  SOCCER = 2,
  VOLLEYBALL = 3,
  BASEBALL = 6,
  NETBALL = 7,
  FIELD_HOCKEY = 8,
  ICE_HOCKEY = 9,
  HANDBALL = 10,
  TENNIS = 11,
  CRICKET_MANUAL = 12,
  BADMINTON = 13,
  BASKETBALL = 14,
  BEACH_VOLLEYBALL = 15,
  FUTSAL = 16,
  INDOOR_CRICKET = 17,
  PADEL = 18,
  PICKLEBALL = 19,
  RUGBY_LEAGUE = 20,
  RUGBY_UNION = 21,
  SOFTBALL = 22,
  SQUASH = 23,
  TABLE_TENNIS = 24,
  AFL = 25,
  WATER_POLO = 26,
}

export type DisciplinaryCard = 'green' | 'yellow' | 'red'

export interface SportConfig {
  id: SPORT_ID
  name: string
  folder: string
  defaultDemoPayload: string
  urlAliases: string[]
  // GoalSport-style configuration (soccer, netball, hockey, etc.). Optional —
  // only set on sports that use the GoalSport template.
  periodCount?: number
  periodLabels?: string[]
  breakLabels?: string[]
  endLabel?: string
  scoreNoun?: string
  disciplinary?: DisciplinaryCard[]
}

interface SportConfigJson {
  id: number
  name: string
  folder: string
  defaultDemoPayload: string
  urlAliases: string[]
  periodCount?: number
  periodLabels?: string[]
  breakLabels?: string[]
  endLabel?: string
  scoreNoun?: string
  disciplinary?: string[]
}

/**
 * Sport configurations indexed by SPORT_ID
 * Loaded from shared sports.json for consistency with server.js
 */
export const SPORTS: Record<number, SportConfig> = Object.fromEntries(
  Object.entries(sportsData.sports as Record<string, SportConfigJson>).map(([key, value]) => [
    Number(key),
    {
      id: value.id as SPORT_ID,
      name: value.name,
      folder: value.folder,
      defaultDemoPayload: value.defaultDemoPayload,
      urlAliases: value.urlAliases,
      periodCount: value.periodCount,
      periodLabels: value.periodLabels,
      breakLabels: value.breakLabels,
      endLabel: value.endLabel,
      scoreNoun: value.scoreNoun,
      disciplinary: value.disciplinary as DisciplinaryCard[] | undefined,
    },
  ])
)

/**
 * Sports that have demo mode support (in display order)
 */
export const DEMO_SPORTS: SPORT_ID[] = sportsData.demoSports as SPORT_ID[]

/**
 * Get sport config by URL parameter value
 * Supports aliases (e.g., 'soccer' maps to FOOTBALL)
 */
export const getSportByUrlParam = (param: string): SPORT_ID | undefined => {
  const normalised = param.toLowerCase()
  for (const sport of Object.values(SPORTS)) {
    if (sport.urlAliases.includes(normalised)) {
      return sport.id
    }
  }
  return undefined
}

/**
 * Get sport config by ID
 */
export const getSportConfig = (sportId: SPORT_ID): SportConfig | undefined => {
  return SPORTS[sportId]
}

/**
 * Get default demo payload path for a sport
 */
export const getDefaultDemoPayload = (sportId: SPORT_ID): string | undefined => {
  return SPORTS[sportId]?.defaultDemoPayload
}
